import { useEffect, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { showToast } from "@/lib/toast";
import { BRAND } from "@/lib/tokens";
import { t } from "@/lib/strings";
import { useUpdateSeries } from "@/hooks/useMeetingSeries";
import { SeriesFormFields } from "@/components/memo/SeriesFormFields";
import type { MeetingSeries, MemoUser } from "@/lib/types";

export function EditSeriesDialog({
  open,
  onOpenChange,
  series,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  series: MeetingSeries;
}) {
  const update = useUpdateSeries(series.id);
  const [name, setName] = useState(series.name);
  const [sections, setSections] = useState(series.section_template.join("\n"));
  const [weekday, setWeekday] = useState<number | null>(series.default_weekday ?? null);
  const [startTime, setStartTime] = useState<string | null>(
    series.default_start_time ? series.default_start_time.slice(0, 5) : null,
  );
  const [participants, setParticipants] = useState<MemoUser[]>(series.participants);
  const [editors, setEditors] = useState<MemoUser[]>(series.editors);

  useEffect(() => {
    if (open) {
      setName(series.name);
      setSections(series.section_template.join("\n"));
      setWeekday(series.default_weekday ?? null);
      setStartTime(series.default_start_time ? series.default_start_time.slice(0, 5) : null);
      setParticipants(series.participants);
      setEditors(series.editors);
    }
  }, [open, series]);

  const canSave = name.trim().length > 0 && !update.isPending;
  const save = () => {
    if (!canSave) return;
    update.mutate(
      {
        name: name.trim(),
        section_template: sections
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        default_weekday: weekday,
        default_start_time: startTime,
        default_participant_ids: participants.map((u) => u.id),
        editor_user_ids: editors.map((u) => u.id),
      },
      {
        onSuccess: () => {
          showToast(t.seriesUpdated, "success");
          onOpenChange(false);
        },
        onError: (e) => showToast(e.message, "error"),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t.seriesEdit} width={520}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 4 }}>
        <SeriesFormFields
          name={name}
          onName={setName}
          sections={sections}
          onSections={setSections}
          weekday={weekday}
          onWeekday={setWeekday}
          startTime={startTime}
          onStartTime={setStartTime}
          participants={participants}
          onParticipants={setParticipants}
          editors={editors}
          onEditors={setEditors}
        />
        <button
          type="button"
          onClick={save}
          disabled={!canSave}
          style={{
            alignSelf: "flex-end",
            background: BRAND.blue,
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "9px 16px",
            fontSize: 13,
            fontWeight: 600,
            cursor: canSave ? "pointer" : "not-allowed",
            opacity: canSave ? 1 : 0.6,
          }}
        >
          {t.seriesEdit}
        </button>
      </div>
    </Dialog>
  );
}
